import { List, Map } from "immutable";
import moment from "moment-timezone";
import { createSelector } from "reselect";
import Angie from "~/utils/angie.jsx";
import { minutesToHours } from "~/utils/utils";
import { getAllJobTypes } from "./selector.jobTypes.jsx";

export const getRawConfigOption = (state, key) => state.config_options.get(`${key}`);

export const getConfigOption = (state, key) => state.config_options.get(`${key}`, Map());

export const getRawPersonalizedConfigOption = (state, key) => {
  return state.personalized_config_options.get(`${key}`);
};

export const getConfigOptions = createSelector(
  (state) => state.config_options,
  (options) => options.toJS()
);

export const getPersonalizedConfigOptions = createSelector(
  (state) => state.personalized_config_options,
  (options) => options.toJS()
);

export const getPersonalizedConfigOption = (state, key) => state.personalized_config_options.get(`${key}`, Map());

export const getPeopleSectionSortMode = createSelector(
  getConfigOptions,
  getPersonalizedConfigOptions,
  (configOptions, personalizedConfigOptions) => {
    const key = "sort_mode_people";

    if (personalizedConfigOptions[key]) {
      return personalizedConfigOptions[key];
    }

    if (configOptions[key]) {
      return configOptions[key];
    }

    return { sort: "name", order: "asc" };
  }
);

export const getArchivedPeopleSortMode = createSelector(
  getConfigOptions,
  getPersonalizedConfigOptions,
  (configOptions, personalizedConfigOptions) => {
    const key = "sort_mode_archived_people";

    if (personalizedConfigOptions[key]) {
      return personalizedConfigOptions[key];
    }

    if (configOptions[key]) {
      return configOptions[key];
    }

    return { sort: "archived", order: "desc" };
  }
);

export const getArchivedCompaniesSortMode = createSelector(
  getConfigOptions,
  getPersonalizedConfigOptions,
  (configOptions, personalizedConfigOptions) => {
    const key = "sort_mode_archived_companies";

    if (personalizedConfigOptions[key]) {
      return personalizedConfigOptions[key];
    }

    if (configOptions[key]) {
      return configOptions[key];
    }

    return { sort: "archived", order: "desc" };
  }
);

const getPersonalizedHomepage = (state) => state.personalized_config_options.get(`homepage`, undefined);

export const getUserDefaultHomepage = createSelector(getPersonalizedHomepage, (defaultHomepage) => {
  switch (defaultHomepage) {
    case `my_work`:
      return `my-work`;
    case `activity`:
      return `activity`;
    case `my_subscription`:
      return `my-updates`;
    case "calendar":
      return "calendar";
    default:
      return `projects`;
  }
});

export const isOnEdgeChannel = (state) => {
  return parseInt(getConfigOption(state, `deployment_channel`)) === 2;
};

export const shouldShowFeaturesInfo = (state) => {
  let show_feature_info = getRawConfigOption(state, "show_features_info");
  return show_feature_info ? show_feature_info : false;
};

export const shouldShowGettingStarted = (state) => {
  return getConfigOption(state, "show_getting_started") ?? false;
};

export const isOnBetaChannel = (state) => {
  return getConfigOption(state, `deployment_channel`) === 1;
};

export const isOnStableChannel = (state) => {
  return getConfigOption(state, `deployment_channel`) === 0;
};

export const isSocketIntegrationEnabled = (state) => {
  let socketIntegration = getConfigOption(state, `socket_integration`);
  if (socketIntegration) {
    return Map.isMap(socketIntegration)
      ? socketIntegration.get(`app_key`) !== null
      : socketIntegration.app_key !== null;
  }
  return false;
};

export const getFirstWeekDay = createSelector(getPersonalizedConfigOptions, (options) => {
  let firstDayOfWeek = options.time_first_week_day;
  firstDayOfWeek = Number(firstDayOfWeek);
  return firstDayOfWeek === 1 ? 1 : 0;
});

export const getSearchSortPreference = (state) => {
  let option = getPersonalizedConfigOption(state, `search_sort_preference`);
  return option === `date` ? `date` : `score`;
};

export const getOnDemandSettings = (state) => getConfigOption(state, `on_demand_settings`);

export const getIsOnDemand = createSelector(getConfigOptions, (config) => {
  if (config && typeof config.on_demand === "boolean") {
    return config.on_demand;
  }
  return undefined;
});

export const makeGetAccountStatus = () => {
  return createSelector(getOnDemandSettings, (onDemandSettings) => {
    return (onDemandSettings && onDemandSettings.get(`account_status`)) || ``;
  });
};

export const makeGetCurrentPlanName = () => {
  return createSelector(getOnDemandSettings, (onDemandSettings) => {
    return onDemandSettings?.get(`current_plan`)?.get(`name`) || ``;
  });
};

export const getCurrentPlan = createSelector(getOnDemandSettings, (onDemandSettings) => {
  return onDemandSettings?.get(`current_plan`);
});

export const makeGetAccountBillingPeriod = () => {
  return createSelector(getOnDemandSettings, (onDemandSettings) => {
    return onDemandSettings && onDemandSettings.get(`billing_period`) ? onDemandSettings.get(`billing_period`) : ``;
  });
};

export const makeGetAccountDiscount = () => {
  return createSelector(getOnDemandSettings, (onDemandSettings) => {
    return onDemandSettings && onDemandSettings.get(`discount`) ? onDemandSettings.get(`discount`) : ``;
  });
};

export const makeGetIsChristmasDiscountActive = () => {
  return createSelector(getOnDemandSettings, (onDemandSettings) => {
    return onDemandSettings && onDemandSettings.get(`is_christmas_discount`)
      ? onDemandSettings.get(`is_christmas_discount`)
      : false;
  });
};

export const makeGetIsAccountLegacy = () => {
  return createSelector(getOnDemandSettings, (onDemandSettings) => {
    return onDemandSettings && onDemandSettings.get(`is_legacy`) ? onDemandSettings.get(`is_legacy`) : false;
  });
};

const getOnDemandSettingsValueByKey = (state, key) => getOnDemandSettings(state).get(`${key}`);

export const getPricingModel = (state) => getOnDemandSettingsValueByKey(state, `pricing_model`);

export const isLifetimePricingModel = (state) => {
  let pricing_model = getPricingModel(state);

  return pricing_model.get("name") === "lifetime_2021";
};

export const getURLTarget = () => ``;

export const getURLHref = () => `subscription/choose-plan`;

export const getCancelSubscriptionURL = () => `/subscription/cancel`;

export const makeGetIsSuspendedSelector = () => {
  const getAccountStatus = makeGetAccountStatus();
  return createSelector(getAccountStatus, (accountStatus) => accountStatus === `suspended`);
};

export const makeGetIsRetiredSelector = () => {
  const getAccountStatus = makeGetAccountStatus();
  return createSelector(getAccountStatus, (accountStatus) => accountStatus === `retired`);
};

export const makeGetIsActiveSelector = () => {
  const getAccountStatus = makeGetAccountStatus();
  return createSelector(getAccountStatus, (accountStatus) => accountStatus === `active`);
};

export const makeGetIsFailedPaymentSelector = () => {
  const getAccountStatus = makeGetAccountStatus();
  return createSelector(getAccountStatus, (accountStatus) => accountStatus === `failed_payment`);
};

export const getUserDefaultJobTypeId = createSelector(
  (state) => getPersonalizedConfigOption(state, `default_job_type_id`),
  getAllJobTypes,
  (defaultJobTypeId, allJobTypes) => {
    let jobType = allJobTypes[`${defaultJobTypeId}`];

    if (jobType && !jobType.is_archived) {
      return jobType.id;
    }

    jobType = Object.values(allJobTypes).find((jobType) => {
      return jobType.is_default && !jobType.is_archived;
    });

    return jobType ? jobType.id : 0;
  }
);

export const getTimeWorkdays = createSelector(
  (state) => getRawConfigOption(state, `time_workdays`),
  (workdays = List()) => workdays.toArray().map((value) => parseInt(value))
);

export const getDaysOfWeekend = createSelector(
  (state) => getRawConfigOption(state, `time_workdays`),
  (workdays = List()) => {
    if (!workdays.size) {
      return [];
    }
    let weekDays = [0, 1, 2, 3, 4, 5, 6];
    let workDays = workdays.toArray().map((value) => parseInt(value));
    return weekDays.filter((weekday) => !workDays.includes(weekday));
  }
);

export const isTaskEstimatesEnabled = (state) => !!getRawConfigOption(state, `task_estimates_enabled`);

export const getDisplayModeConfigOption = createSelector(
  (state, page) => getRawPersonalizedConfigOption(state, `display_mode_${page}`),
  (value = Map()) => (typeof value?.toJS === "function" ? value?.toJS() : {})
);

export const getDisplayModeProjectTasksConfigOption = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `display_mode_project_tasks`),
  (value = Map()) => (Map.isMap(value) ? value.toJS() : { 0: value })
);

export const getDisplayModeProjectTasksByProjectId = createSelector(
  getDisplayModeProjectTasksConfigOption,
  (state, projectId) => projectId,
  (displayModes, projectId) => {
    return displayModes?.[projectId] ? displayModes[projectId] : "list";
  }
);

export const getSortByProjectFilesConfigOption = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `sort_mode_projects_files`),
  (value = Map()) => {
    return Map.isMap(value) ? value.toJS() : { sort: "created", order: "desc" };
  }
);

export const getSortModeEstimatesConfigOption = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `sort_mode_estimates`),
  (value = Map()) => {
    return Map.isMap(value) ? value.toJS() : { sort: "date", order: "desc" };
  }
);

export const getFilterEstimatesConfigOption = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `filter_estimates`),
  (value = Map()) => {
    return Map.isMap(value) ? value.toJS() : {};
  }
);

export const getFilterCalendarConfigOption = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `filter_calendar`),
  (value = Map()) => {
    return Map.isMap(value) ? value.toJS() : {};
  }
);

export const getSortByProjectDiscussionsConfigOption = createSelector(getPersonalizedConfigOptions, (configOptions) => {
  return configOptions.sort_mode_project_discussions ? configOptions.sort_mode_project_discussions : {};
});

export const getConfigOptionForSortNotes = createSelector(getPersonalizedConfigOptions, (configOptions) => {
  return configOptions.sort_mode_project_notes ? configOptions.sort_mode_project_notes : {};
});

export const getSortNotesConfigOption = createSelector(
  getConfigOptionForSortNotes,
  (state, projectId) => projectId,
  (config, projectId) => {
    if (config[projectId]) return config[projectId];
    return config["default"] || { sort: "updated", order: "desc" };
  }
);

export const getDiscussionSortByProjectIdConfigOption = createSelector(
  getSortByProjectDiscussionsConfigOption,
  (state, projectId) => projectId,
  (config, projectId) => {
    if (config[projectId]) return config[projectId];
    return config["default"] || { sort: "updated", order: "desc" };
  }
);

export const getFilterProjectFilesConfigOption = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `filter_project_files`),
  (value = Map()) => {
    return Map.isMap(value) ? value.toJS() : {};
  }
);

export const getFilterProjectTimeConfigOption = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `filter_project_time`),
  (value = Map()) => {
    return Map.isMap(value) ? value.toJS() : {};
  }
);

export const getFilterProjectExpenseConfigOption = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `filter_project_expense`),
  (value = Map()) => {
    return Map.isMap(value) ? value.toJS() : {};
  }
);

export const getPersonalizedDateFormat = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `format_date`),
  (value = "YYYY-MM-DD") => Angie.functions.phpToMomentDateFormat(value)
);

export const getPersonalizedTimeFormat = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `format_time`),
  (value = "HH:mm") => Angie.functions.phpToMomentTimeFormat(value)
);

export const getUpdateHideNotifications = createSelector(
  (state) => getRawPersonalizedConfigOption(state, "updates_hide_notifications"),
  (value = false) => value
);

export const getMyWorkCollapsedProjects = createSelector(
  (state) => getRawPersonalizedConfigOption(state, "my_work_collapsed_projects"),
  (value) => (value ? value.toArray() : [])
);

export const getMyWorkCollapsedTaskLists = createSelector(
  (state) => getRawPersonalizedConfigOption(state, "my_work_collapsed_task_lists"),
  (value) => (value ? value : [])
);

export const getMyWorkCollapsedTaskDateGroups = createSelector(
  (state) => getRawPersonalizedConfigOption(state, "my_work_collapsed_tasks_date_groups"),
  (value) => (value ? value : [])
);

export const getCollapsedProjectGroups = createSelector(
  (state) => getRawPersonalizedConfigOption(state, "project_groups_collapsed"),
  (value) => (value ? value : [])
);

export const makeGetIsFreePlan = () => {
  return createSelector(getOnDemandSettings, (onDemandSettings) => {
    return onDemandSettings?.get(`current_plan`)?.get(`name`) === "free";
  });
};

export const getIsTrial = createSelector(getOnDemandSettings, (onDemandSettings) => {
  if (onDemandSettings.has("account_status") && onDemandSettings.has("is_paid")) {
    return onDemandSettings.get("account_status") === "trial" && !onDemandSettings.get("is_paid");
  }
  return false;
});

export const isProjectsFilterOn = createSelector(
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_client"),
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_label"),
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_category"),
  (state) => getRawPersonalizedConfigOption(state, "filter_projects_leader"),
  (filterByClient, filterByLabel, filterByCategory, filterByLead) => {
    return filterByClient.size > 0 || filterByLabel.size > 0 || filterByCategory.size > 0 || filterByLead.size > 0;
  }
);

export const isClosedProjectsFilterOn = createSelector(
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_client"),
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_label"),
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_category"),
  (state) => getRawPersonalizedConfigOption(state, "filter_completed_projects_leader"),
  (filterByClient, filterByLabel, filterByCategory, filterByLead) => {
    return filterByClient.size > 0 || filterByLabel.size > 0 || filterByCategory.size > 0 || filterByLead.size > 0;
  }
);

export const getAppliedFilterCount = createSelector(
  (state, key) => getPersonalizedConfigOption(state, key),
  (selectedFilters) => selectedFilters.size
);

export const getTotalProjectsFiltersApplied = createSelector(
  (state) => getAppliedFilterCount(state, "filter_projects_client"),
  (state) => getAppliedFilterCount(state, "filter_projects_label"),
  (state) => getAppliedFilterCount(state, "filter_projects_category"),
  (state) => getAppliedFilterCount(state, "filter_projects_leader"),
  (clientFilters, labelFilters, categoryFilters, leaderFilter) =>
    clientFilters + labelFilters + categoryFilters + leaderFilter
);

export const getTotalCompletedProjectsFiltersApplied = createSelector(
  (state) => getAppliedFilterCount(state, "filter_completed_projects_client"),
  (state) => getAppliedFilterCount(state, "filter_completed_projects_label"),
  (state) => getAppliedFilterCount(state, "filter_completed_projects_category"),
  (state) => getAppliedFilterCount(state, "filter_completed_projects_leader"),
  (clientFilters, labelFilters, categoryFilters, leaderFilter) =>
    clientFilters + labelFilters + categoryFilters + leaderFilter
);

export const getSocketIntegration = createSelector(
  (state) => state.config_options?.get("socket_integration"),
  (socketIntegration) => socketIntegration?.toJS()
);

export const getExternalLoginUrl = (state) =>
  state.personalized_config_options?.get("login_policy")?.get("external_login_url");
export const getExternalLogoutUrl = (state) =>
  state.personalized_config_options?.get("login_policy")?.get("external_logout_url");
export const getCsrfValidatorName = (state) => state.config_options?.get("csrf_validator_cookie_name");

export const getHiddenMainMenuOptions = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `hidden_main_menu_options`),
  (items = List()) => items.toJS()
);

export const getMainMenuOptionsOrder = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `main_menu_options_order`),
  (items = List()) => items.toJS()
);

export const getGoogleDriveConfig = (state) => {
  return getConfigOption(state, "google_drive");
};

export const getDropboxConfig = (state) => {
  return getConfigOption(state, "dropbox_app_key");
};

export const getAllProjectFilters = (state) => state.personalized_config_options.get(`project_task_filters`, Map());

export const getProjectFiltersById = (state, id) => {
  return getAllProjectFilters(state)?.get(`${id}`, Map());
};

export const getSelectedTaskListIdFilterSelector = createSelector(getProjectFiltersById, (selectedFilters) =>
  selectedFilters && selectedFilters.has(`taskListFilter`) ? selectedFilters.get(`taskListFilter`)?.toArray() : []
);

export const makeSelectedPriorityFilterSelector = () => {
  return createSelector(getProjectFiltersById, (selectedFilters) =>
    selectedFilters && selectedFilters.has(`priorityFilter`) ? selectedFilters.get("priorityFilter")?.toArray() : []
  );
};

export const makeSelectedLabelIdFilterSelector = () => {
  return createSelector(getProjectFiltersById, (selectedFilters) =>
    selectedFilters && selectedFilters.has(`labelFilter`) ? selectedFilters.get(`labelFilter`)?.toArray() : []
  );
};

export const makeSelectedDateIdFilterSelector = () => {
  return createSelector(getProjectFiltersById, (selectedFilters) =>
    selectedFilters && selectedFilters.has(`dateFilter`) ? selectedFilters.get(`dateFilter`)?.toArray() : []
  );
};

export const makeSelectedAssigneeIdFilterSelector = () => {
  return createSelector(getProjectFiltersById, (selectedFilters) =>
    selectedFilters && selectedFilters.has(`peopleFilter`) ? selectedFilters.get(`peopleFilter`)?.toArray() : []
  );
};

export const makeGetProjectFiltersByTaskListIdSelector = () =>
  createSelector(
    getAllProjectFilters,
    (state, id) => state.task_lists.get(`${id}`, Map()).get(`project_id`),
    (projectFilters, projectId) => projectFilters?.get(`${projectId}`)
  );

export const getIsChatEnabled = createSelector(
  (state) => getRawConfigOption(state, `chat_enabled`),
  (state) => getSocketIntegration(state),
  (featureEnabled, socketIntegration) => {
    return featureEnabled && !!socketIntegration;
  }
);

export const getIsFeatureEnabled = createSelector(
  (state, feature) => feature,
  getConfigOptions,
  (feature, allConfigOptions) => {
    return !!allConfigOptions[`${feature}_enabled`];
  }
);

export const getDefaultWeeklyCapacity = createSelector(
  (state) => getConfigOption(state, `weekly_worker_capacity`),
  (weeklyWorkerCapacity) => {
    const weeklyCapacity = weeklyWorkerCapacity.reduce((sumCapacity, capacity) => sumCapacity + capacity, 0);

    return minutesToHours(weeklyCapacity, true);
  }
);

export const getIsHotJarEnabled = createSelector(getOnDemandSettings, (onDemandSettings) => {
  return onDemandSettings && onDemandSettings.has("hotjar_for_next_enabled")
    ? onDemandSettings.get("hotjar_for_next_enabled")
    : false;
});

export const getEstimateCollapsedStatus = createSelector(getPersonalizedConfigOptions, (options) => {
  if (Array.isArray(options.estimate_collapsed_status)) {
    return options.estimate_collapsed_status;
  }
  return [];
});

export const getTimezoneInfo = createSelector(getPersonalizedConfigOptions, (options) => {
  let { timezone, timezone_autodetect, time_timezone, time_timezone_autodetect } = options;

  if (typeof time_timezone_autodetect === "undefined" && typeof timezone_autodetect !== "undefined") {
    time_timezone_autodetect = timezone_autodetect;
  }
  if (typeof time_timezone === "undefined" && typeof timezone !== "undefined") {
    time_timezone = timezone;
  }
  return {
    time_timezone_autodetect,
    time_timezone,
  };
});

export const getUserTimezone = createSelector(getTimezoneInfo, (timezoneInfo) => {
  if (timezoneInfo.time_timezone_autodetect) {
    return moment.tz.guess();
  } else if (timezoneInfo?.time_timezone) {
    return timezoneInfo.time_timezone;
  } else {
    return moment.tz.guess();
  }
});
